/**
 * PotApprovalsListView Organism
 * Pure presentation component for pot approvals list.
 * Title, period nav, and search have moved to PageHeader / PageToolbar.
 */

import { useCallback, useMemo } from 'react';
import {
  AccountBalanceWalletIcon,
  Alert,
  Band,
  Button,
  Collapse,
  LedgerHeader,
  Spinner,
  StatusDot,
  ledgerContainerClass,
  statusToken,
  type LedgerCollapse,
  type LedgerColumn,
} from '@billda/design-system';
import { useShallow } from 'zustand/react/shallow';
import { PotApprovalListItem } from './PotApprovalListItem';
import { ListViewShell } from '../../../shared/components/ui/ListViewShell';
import { groupPotsByStatus } from '../utils/groupPotsByStatus';
import { usePotApprovalsUIStore } from '../stores/potApprovalsUIStore';
import type { PotSummary, PotStatus } from '../types/pot.types';

export interface PotApprovalsDataState {
  pots: PotSummary[];
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
}

export interface PotApprovalsListViewProps {
  data: PotApprovalsDataState;
  onLoadMore: () => void;
  onPotClick?: (potId: string) => void;
  onMoveToActivePeriod?: (potId: string) => void;
  onMoveToPrimary?: (potId: string) => void;
  onExtendDeadline?: (potId: string) => void;
  searchText: string;
}

const POTS_COLLAPSE: LedgerCollapse = {
  container: 'pots-collection',
  at: 620,
};

const POTS_COLUMNS: LedgerColumn[] = [
  { key: 'type', label: 'Type', flex: 0.65, hideBelow: 900 },
  { key: 'net', label: 'Net', flex: 0.8, hideBelow: 0, align: 'end' },
  { key: 'deadline', label: 'Next deadline', flex: 1.25, hideBelow: 720 },
  { key: 'flags', label: 'Flags', flex: 0.65 },
];

export function PotApprovalsListView({
  data,
  onLoadMore,
  onPotClick,
  onMoveToActivePeriod,
  onMoveToPrimary,
  onExtendDeadline,
  searchText,
}: PotApprovalsListViewProps) {
  const { pots, hasMore, isLoading, isLoadingMore, error } = data;

  const { collapsedStatuses, groupingMode, toggleStatusGroup } =
    usePotApprovalsUIStore(
      useShallow(state => ({
        collapsedStatuses: state.collapsedStatuses,
        groupingMode: state.groupingMode,
        toggleStatusGroup: state.toggleStatusGroup,
      }))
    );

  const groups = useMemo(() => groupPotsByStatus(pots), [pots]);

  const handleToggle = useCallback(
    (status: PotStatus) => () => toggleStatusGroup(status),
    [toggleStatusGroup]
  );

  const renderPot = (pot: PotSummary) => (
    <PotApprovalListItem
      key={pot.id}
      pot={pot}
      columns={POTS_COLUMNS}
      collapse={POTS_COLLAPSE}
      onClick={() => onPotClick?.(pot.id)}
      onMoveToActivePeriod={onMoveToActivePeriod}
      onMoveToPrimary={onMoveToPrimary}
      onExtendDeadline={onExtendDeadline}
      data-testid={`pot-${pot.id}`}
    />
  );

  if (error && !isLoading && pots.length === 0) {
    return (
      <div className="p-4">
        <Alert severity="error">Failed to load pots. Please try again.</Alert>
      </div>
    );
  }

  return (
    <div
      className={`flex h-full min-h-0 flex-col overflow-auto ${ledgerContainerClass(POTS_COLLAPSE.container)}`}
    >
      <ListViewShell
        itemCount={pots.length}
        loading={isLoading}
        emptyIcon={
          <AccountBalanceWalletIcon
            aria-hidden="true"
            className="size-12 text-content-secondary"
          />
        }
        emptyTitle={
          searchText ? 'No pots match your search' : 'No pots this week'
        }
        emptyHelperText={
          searchText
            ? 'Try a different search term or clear your search to see all pots for this period.'
            : 'Pots appear here once workers book and submit work for the selected period. Use the week picker to check other periods.'
        }
        emptyMessage=""
        data-testid="pot-approvals-list"
      >
        <LedgerHeader
          columns={POTS_COLUMNS}
          collapse={POTS_COLLAPSE}
          titleLabel="Owner"
          showValues={false}
          showActions
          hideBelow={POTS_COLLAPSE.at}
        />
        {groupingMode === 'none' && pots.map(renderPot)}
        {groupingMode === 'status' &&
          groups.map(group => {
            const isCollapsed = collapsedStatuses.has(group.status);
            const panelId = `pot-group-panel-${group.status}`;
            return (
              <div key={group.status}>
                <Band
                  collapse={POTS_COLLAPSE}
                  primary={statusToken(group.status).word}
                  lead={<StatusDot color={statusToken(group.status).color} />}
                  count={group.pots.length}
                  open={!isCollapsed}
                  onToggle={handleToggle(group.status)}
                  panelId={panelId}
                  showValues={false}
                  showActions
                  data-testid={`status-group-${group.status}`}
                />
                <Collapse in={!isCollapsed} unmountOnExit>
                  <div id={panelId}>{group.pots.map(renderPot)}</div>
                </Collapse>
              </div>
            );
          })}
      </ListViewShell>

      {error && !isLoading && pots.length > 0 && (
        <Alert severity="error" className="mx-4 mb-2">
          Failed to load more pots. Please try again.
        </Alert>
      )}

      {/* Load More Button */}
      {hasMore && !isLoading && (
        <div className="flex justify-center p-4">
          <Button
            variant="outline"
            onClick={onLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore && <Spinner size={20} />}
            {isLoadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  );
}
